/**
 * Control Budget Model — a single-value node under a Concept, sibling to Units
 * (ConceptUnitsSection) and Development Metrics (ConceptDevMetricsSection): the Control Budget
 * Model assigned to this concept, shown inline, with an ⋯ menu to assign one (or change it if
 * one is already assigned).
 *
 * Control Budget Models (tblEdUZrWv4mbudzU) is a small, admin-curated $/SF cost-template catalog
 * — the SAME table already powering the presentation Budget Explorer (present/BudgetExplorer.jsx)
 * — NOT the separate 'Control Budgets' table (tblO0jRQhOKevko1A, per-GC bid estimates tracked
 * against a project/concept). Similarly named, unrelated tables; this node is about the Model.
 *
 * The assignment lives directly on the Project Concept record's own 'Control Budget Model' link
 * field (multipleRecordLinks, used here as a single-select), so this reads straight from
 * ctx.conceptCache — already loaded, no separate fetch — and writes via
 * services/design.js#updateConcept, exactly like every other concept field
 * (see ConceptDevMetricsSection.jsx).
 *
 * Expandable (2026-07): once a model is assigned, the row gets a real expand triangle revealing
 * a READ-ONLY key-info summary (category $/SF ranges, scope factors, gross area factor) via
 * ControlBudgetModelDetail — lazy-loaded full records (getControlBudgetModelsFull) only fetched
 * on first expand, cached separately from the minimal { id, name } picker list
 * (useControlBudgetModels) so the picker's shape stays untouched. Nothing here is editable —
 * that stays exclusively in the admin ControlBudgetModelsEditor.jsx.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'
import { getControlBudgetModels, getControlBudgetModelsFull, updateConcept } from '../services/design.js'
import SearchablePicker from './SearchablePicker.jsx'

/** Control Budget Models barely change — fetch once per page load, shared by every picker. */
function useControlBudgetModels() {
  const [models, setModels] = useState(ctx.controlBudgetModelsCache || null)
  useEffect(() => {
    if (ctx.controlBudgetModelsCache) return
    getControlBudgetModels()
      .then(list => { ctx.controlBudgetModelsCache = list; setModels(list) })
      .catch(() => setModels([]))
  }, [])
  return models
}

// Same catalog, full records (every $/SF + factor field) — only fetched once the row is expanded.
// Separate cache from useControlBudgetModels()'s minimal { id, name } shape used by the picker.
function useControlBudgetModelsFull(shouldLoad) {
  const [models, setModels] = useState(ctx.controlBudgetModelsFullCache || null)
  useEffect(() => {
    if (!shouldLoad || ctx.controlBudgetModelsFullCache) return
    getControlBudgetModelsFull()
      .then(list => { ctx.controlBudgetModelsFullCache = list; setModels(list) })
      .catch(() => setModels([]))
  }, [shouldLoad])
  return models
}

const BUDGET_CATEGORIES = ['Occupiable', 'Garage', 'Storage Utility', 'Elevated Deck', 'Covered Patio', 'Site Improvement']
const BUDGET_SCOPE_FACTORS = [
  { field: 'Cosmetic Refresh Factor', label: 'Cosmetic Refresh' },
  { field: 'Light Alteration Factor', label: 'Light Alteration' },
  { field: 'Moderate Alteration Factor', label: 'Moderate Alteration' },
  { field: 'Heavy Alteration Factor', label: 'Heavy Alteration' },
  { field: 'Major Alteration Factor', label: 'Major Alteration' },
  { field: 'Existing to Remain Factor', label: 'Existing to Remain' },
]

function fmtNum(v, opts) {
  if (v == null || v === '') return '—'
  return opts?.money ? '$' + Number(v).toLocaleString() : Number(v).toLocaleString()
}

/** Read-only key-info summary for the assigned model — editing stays in the admin
 *  Control Budget Models editor (ControlBudgetModelsEditor.jsx). */
function ControlBudgetModelDetail({ model }) {
  if (!model) return <div className="wf-loading">Loading model…</div>
  const f = model.fields
  return (
    <>
      {BUDGET_CATEGORIES.map(cat => {
        const low = f[cat + ' Cost Per SF Low'], high = f[cat + ' Cost Per SF High']
        if (low == null && high == null) return null
        return (
          <div key={cat} className="wf-node wf-node--item" data-key={'control-budget-detail:' + model.id + ':' + cat}>
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
              <div className="wf-text-wrap">
                <span className="wf-node-text">{cat}</span>
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8, color: 'var(--aj-text-mid)' }}>
                  {fmtNum(low, { money: true })} – {fmtNum(high, { money: true })} /sf
                </span>
              </div>
            </div>
          </div>
        )
      })}
      {f['Gross Area Factor'] != null && (
        <div className="wf-node wf-node--item" data-key={'control-budget-detail:' + model.id + ':gaf'}>
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
            <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
            <div className="wf-text-wrap">
              <span className="wf-node-text">Gross Area Factor</span>
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8, color: 'var(--aj-text-mid)' }}>
                {fmtNum(f['Gross Area Factor'])}
              </span>
            </div>
          </div>
        </div>
      )}
      {BUDGET_SCOPE_FACTORS.filter(sf => f[sf.field] != null).map(sf => (
        <div key={sf.field} className="wf-node wf-node--item" data-key={'control-budget-detail:' + model.id + ':' + sf.field}>
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
            <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
            <div className="wf-text-wrap">
              <span className="wf-node-text">{sf.label}</span>
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8, color: 'var(--aj-text-mid)' }}>
                {fmtNum(f[sf.field])}
              </span>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

function ControlBudgetModelPickerModal({ models, currentId, onAssign, onUnassign, onClose }) {
  const [pending, setPending] = useState(currentId || '')
  const [busy, setBusy] = useState(false)

  const inp = {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12,
    padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)',
    outline: 'none', boxSizing: 'border-box', width: '100%',
  }

  async function handleSave() {
    if (!pending || pending === currentId) { onClose(); return }
    setBusy(true)
    try { await onAssign(pending) } finally { setBusy(false) }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={busy ? undefined : onClose}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 380, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          {currentId ? 'Change control budget model' : 'Assign control budget model'}
        </div>
        <SearchablePicker
          items={models ? models.map(m => ({ id: m.id, label: m.name })) : null}
          value={pending}
          onChange={setPending}
          placeholder="Search models…"
          inputStyle={inp}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          {currentId && (
            <button disabled={busy} onClick={async () => { setBusy(true); try { await onUnassign() } finally { setBusy(false) } }}
              style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 14px', border: '0.5px solid rgba(184,39,44,0.35)', borderRadius: 4, background: 'transparent', color: '#b8272c', cursor: busy ? 'default' : 'pointer' }}>
              Remove assignment
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} disabled={busy} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: busy ? 'default' : 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={busy || !pending || pending === currentId}
            style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: (busy || !pending || pending === currentId) ? 'default' : 'pointer', opacity: (busy || !pending || pending === currentId) ? 0.6 : 1 }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default function ControlBudgetSection({ conceptId }) {
  const [, setTick] = useState(0)
  const bump = () => setTick(t => t + 1)
  const [pickerOpen, setPickerOpen] = useState(false)
  const menu = useActionMenu()
  const models = useControlBudgetModels()
  const key = 'control-budget:' + conceptId
  const [collapsed, setCollapsed] = useState(() => wf.collapsed[key] !== false)
  function toggle() { setCollapsed(c => { const n = !c; wf.collapsed[key] = n; saveViewState(); return n }) }

  // Re-render when the concept cache is populated/updated — this component has no fetch of its
  // own (mirrors ConceptDevMetricsSection.jsx).
  useEffect(() => {
    function onRender() { bump() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  const concept = (ctx.conceptCache || []).find(c => c.id === conceptId)
  const assignedId = concept ? ((concept.fields['Control Budget Model'] || [])[0] || '') : ''
  const assignedModel = assignedId ? (models || []).find(m => m.id === assignedId) : null
  const canExpand = !!assignedModel
  const fullModels = useControlBudgetModelsFull(canExpand && !collapsed)
  const fullModel = assignedId ? (fullModels || []).find(m => m.id === assignedId) : null

  async function persist(fields) {
    const updated = await updateConcept(conceptId, fields)
    if (Array.isArray(ctx.conceptCache)) {
      const idx = ctx.conceptCache.findIndex(c => c.id === conceptId)
      if (idx !== -1) ctx.conceptCache[idx] = { ...ctx.conceptCache[idx], fields: { ...ctx.conceptCache[idx].fields, ...updated.fields } }
    }
    ctx.render()
  }
  async function handleAssign(modelId) { await persist({ 'Control Budget Model': [modelId] }); setPickerOpen(false) }
  async function handleUnassign() { await persist({ 'Control Budget Model': [] }); setPickerOpen(false) }

  return (
    <div className={`wf-node wf-node--item${canExpand && collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        {canExpand ? (
          <button className="wf-triangle" onClick={toggle}>
            <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
          </button>
        ) : (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        )}
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">Control Budget Model</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: assignedModel ? 'var(--aj-text-mid)' : 'var(--aj-text-muted)',
            fontStyle: assignedModel ? 'normal' : 'italic',
          }}>
            {!concept ? 'Loading…' : (assignedModel ? assignedModel.name : 'Not assigned')}
          </span>
        </div>
        {concept && <ActionMenuButton menu={menu} />}
      </div>
      {canExpand && !collapsed && (
        <div className="wf-children">
          <ControlBudgetModelDetail model={fullModel} />
        </div>
      )}
      {concept && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); setPickerOpen(true) }}>
            {assignedModel ? 'Change control budget model' : 'Assign control budget model'}
          </ActionMenuItem>
        </ActionMenu>
      )}
      {pickerOpen && (
        <ControlBudgetModelPickerModal
          models={models}
          currentId={assignedId}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}
